import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { api } from "@/lib/api";
import {
  collectImageItems,
  collectTextItems,
  extractEditableImage,
  findTextOverrideTargets,
  normalizePagePath,
  replaceTextNodeValue,
} from "./content-dom";

const REFRESH_INTERVAL_MS = 60 * 1000;

function applyImageOverride(item, override) {
  const element = item.element;
  if (item.imageType === "background") {
    if (element.style.backgroundImage.includes(override.replacement_value)) {
      return;
    }
    element.style.backgroundImage = element.style.backgroundImage.replace(
      item.sourceValue,
      override.replacement_value,
    );
  } else {
    if (element.getAttribute("src") !== override.replacement_value) {
      element.setAttribute("src", override.replacement_value);
    }
  }
}

function applyOverrides(overrides) {
  const textItems = collectTextItems();
  const imageItems = collectImageItems();
  overrides.forEach((override) => {
    if (override.content_kind === "text") {
      const targets = findTextOverrideTargets(override, textItems);
      targets.forEach(({ node }) => {
        const current = node.nodeValue?.trim();
        if (current === override.source_value || current === override.replacement_value) {
          replaceTextNodeValue(node, current, override.replacement_value);
        }
      });
      return;
    }

    const located = imageItems.find((item) => item.locator === override.locator);
    const targets = located
      ? [located]
      : imageItems.filter((item) => item.sourceValue === override.source_value);
    targets.forEach((item) => applyImageOverride(item, override));
  });
}

function findItemOverride(item, overrides) {
  const matchesValue = (entry) => entry.content_kind === item.kind &&
    [entry.source_value, entry.replacement_value].includes(item.sourceValue);
  return overrides.find((entry) => entry.locator === item.locator && matchesValue(entry)) ||
    overrides.find((entry) => matchesValue(entry));
}

function buildInventory(overrides) {
  return [...collectTextItems(), ...collectImageItems()].map((item) => {
    const override = findItemOverride(item, overrides);
    return {
      kind: item.kind,
      imageType: item.imageType,
      locator: override?.locator || item.locator,
      sourceValue: override?.source_value || item.sourceValue,
      currentValue: override?.replacement_value || item.sourceValue,
      overrideId: override?.id || null,
    };
  });
}

export default function ContentRuntime() {
  const location = useLocation();
  const overridesRef = useRef([]);
  const signatureRef = useRef("");
  const editorMode = new URLSearchParams(location.search).get("contentEditor") === "1";
  const pagePath = normalizePagePath(location.pathname);

  useEffect(() => {
    if (pagePath.startsWith("/admin")) {
      return undefined;
    }
    let active = true;
    let observer;
    let applying = false;
    let applyTimer;
    overridesRef.current = [];
    signatureRef.current = "";

    const publishInventory = () => {
      if (!editorMode || window.parent === window) {
        return;
      }
      window.parent.postMessage(
        {
          type: "polenica:content-inventory",
          pagePath,
          items: buildInventory(overridesRef.current),
        },
        window.location.origin,
      );
    };

    const applyAndPublish = () => {
      if (!active || applying) {
        return;
      }
      applying = true;
      applyOverrides(overridesRef.current);
      publishInventory();
      window.setTimeout(() => {
        applying = false;
      }, 0);
    };

    const loadOverrides = async () => {
      try {
        const response = await api.get("/content/overrides", { params: { page_path: pagePath } });
        if (active) {
          const nextOverrides = response.data || [];
          const nextSignature = JSON.stringify(nextOverrides);
          if (signatureRef.current && signatureRef.current !== nextSignature) {
            window.location.reload();
            return;
          }
          signatureRef.current = nextSignature;
          overridesRef.current = nextOverrides;
          applyAndPublish();
        }
      } catch (_error) {
        // The editorial layer must never prevent a functional screen from rendering.
      }
    };

    observer = new MutationObserver(() => {
      window.clearTimeout(applyTimer);
      applyTimer = window.setTimeout(applyAndPublish, 80);
    });
    const root = document.getElementById("root");
    if (root) {
      observer.observe(root, {
        attributes: true,
        attributeFilter: ["src", "style"],
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
    loadOverrides();
    const intervalId = window.setInterval(loadOverrides, REFRESH_INTERVAL_MS);

    const selectItem = (event) => {
      if (!editorMode || window.parent === window) {
        return;
      }
      const image = extractEditableImage(event.target);
      const text = collectTextItems(event.target).find((item) => item.element === event.target);
      const selected = image || text;
      if (!selected) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const override = findItemOverride(selected, overridesRef.current);
      window.parent.postMessage(
        {
          type: "polenica:content-selected",
          item: {
            kind: selected.kind,
            imageType: selected.imageType,
            locator: override?.locator || selected.locator,
            sourceValue: override?.source_value || selected.sourceValue,
            currentValue: override?.replacement_value || selected.sourceValue,
            overrideId: override?.id || null,
          },
        },
        window.location.origin,
      );
    };
    document.addEventListener("click", selectItem, true);

    return () => {
      active = false;
      observer?.disconnect();
      document.removeEventListener("click", selectItem, true);
      window.clearTimeout(applyTimer);
      window.clearInterval(intervalId);
    };
  }, [editorMode, pagePath]);

  return null;
}
